import { fmtHours as fmtNumber } from "../Projects/ProjectViewer/BudgetEditorTab";

// Date and hours helpers for the My Time Entry page. Weeks run Monday–Sunday and
// are keyed by their Monday's ISO date (YYYY-MM-DD, local time).

export const MON3 = [
	"Jan",
	"Feb",
	"Mar",
	"Apr",
	"May",
	"Jun",
	"Jul",
	"Aug",
	"Sep",
	"Oct",
	"Nov",
	"Dec",
];
export const MONTHS = [
	"January",
	"February",
	"March",
	"April",
	"May",
	"June",
	"July",
	"August",
	"September",
	"October",
	"November",
	"December",
];
export const DOW3 = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export const startOfDay = (d) =>
	new Date(d.getFullYear(), d.getMonth(), d.getDate());
export const addDays = (d, n) =>
	new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
export const sameDay = (a, b) =>
	a.getFullYear() === b.getFullYear() &&
	a.getMonth() === b.getMonth() &&
	a.getDate() === b.getDate();
export const mondayOf = (d) => addDays(startOfDay(d), -((d.getDay() + 6) % 7));
export const dayIndexOf = (d) => (d.getDay() + 6) % 7;

export const isoOf = (d) =>
	d.getFullYear() +
	"-" +
	String(d.getMonth() + 1).padStart(2, "0") +
	"-" +
	String(d.getDate()).padStart(2, "0");
export const fromIso = (s) => {
	const [y, m, d] = s.split("-").map(Number);
	return new Date(y, m - 1, d);
};

export function buildWeek(weekKey, today) {
	const monday = fromIso(weekKey);
	return Array.from({ length: 7 }, (_, i) => {
		const date = addDays(monday, i);
		return {
			date,
			iso: isoOf(date),
			name: DOW3[i],
			short: date.getMonth() + 1 + "/" + date.getDate(),
			long: DOW3[i] + ", " + MON3[date.getMonth()] + " " + date.getDate(),
			isToday: sameDay(date, today),
			// Finished days only — today isn't flagged as short while it's still in progress
			isPast: date < today,
			isWeekend: i >= 5,
		};
	});
}

export function weekLabel(week) {
	const a = week[0].date;
	const b = week[6].date;
	return (
		"Week of " +
		MON3[a.getMonth()] +
		" " +
		a.getDate() +
		" – " +
		MON3[b.getMonth()] +
		" " +
		b.getDate() +
		", " +
		b.getFullYear()
	);
}

// Every date shown in a month grid, padded to whole Monday-first weeks
export function monthGrid(month) {
	const start = mondayOf(new Date(month.getFullYear(), month.getMonth(), 1));
	const last = new Date(month.getFullYear(), month.getMonth() + 1, 0);
	const count =
		Math.ceil((Math.round((last - start) / 86400000) + 1) / 7) * 7;
	return Array.from({ length: count }, (_, k) => addDays(start, k));
}

// The weekly target comes from the employee record's hours_per_week —
// same rule as the PM Dashboard and Schedule tab; 40/wk when unset.
export const weeklyTargetFor = (employee) =>
	employee && employee.hours_per_week ? Number(employee.hours_per_week) : 40;

// Hours inputs accept partial decimals while typing ("", "1.", ".5")
export const HOURS_INPUT_RE = /^[0-9]*\.?[0-9]*$/;

// Time is sent to the tenth, so a box takes one decimal ("1.04" can't be
// typed). A value already in BQE with more (0.25) can still be edited
// without losing a place.
const decimalsOf = (v) => (String(v).split(".")[1] || "").length;
export const hoursInputOk = (next, prev = "") =>
	HOURS_INPUT_RE.test(next) &&
	decimalsOf(next) <= Math.max(1, decimalsOf(prev));

export const toHours = (v) => {
	const n = parseFloat(v);
	return isNaN(n) ? 0 : n;
};

// Plain numbers ("4", "3.5", "0.75") via the site-wide formatter; grid cells
// show "—" for an empty total
export { fmtNumber };
export const fmtHours = (n) => (n ? fmtNumber(n) : "—");

export const addHours = (a, b) =>
	String(Math.round((toHours(a) + toHours(b)) * 100) / 100);

export const appendMemo = (existing, addition) => {
	const a = (existing || "").trim();
	const b = (addition || "").trim();
	return a ? (b ? a + "; " + b : a) : b;
};

// Project search: name, client or project number
// Searches the tools name and the BQE name, so BQE codes (PJ-…) still match
export const matchesProject = (p, q) =>
	[p.name, p.bqeName, p.client, p.number].join(" ").toLowerCase().includes(q);

// MSAL can't renew the sign-in silently (e.g. after a long idle)
export const signInExpired = (error) =>
	!!error &&
	(error.name === "InteractionRequiredAuthError" ||
		/interaction_required|login_required|consent_required/.test(
			String(error.errorCode || ""),
		));

// "10:42 AM", or "Sep 28, 10:42 AM" when it isn't today
export const fmtUpdatedAt = (ms) => {
	const d = new Date(ms);
	const time = d.toLocaleTimeString([], {
		hour: "numeric",
		minute: "2-digit",
	});
	return sameDay(d, new Date())
		? time
		: d.toLocaleDateString([], { month: "short", day: "numeric" }) +
				", " +
				time;
};

export const hasEntry = (cell) => toHours(cell.hours) > 0 || !!cell.memo.trim();

// A line appears on a mobile day if it has an entry that day or was added
// on that day
export const onDay = (row, day) =>
	hasEntry(row.cells[day]) || (row.days || []).includes(day);

// A blank day cell; BQE-loaded cells also carry `entries` and `lockReason`
export const emptyCell = (billable = true) => ({
	hours: "",
	memo: "",
	billable,
	overtime: false,
	extraTime: false,
	compTime: false,
});
